import React, { useEffect, useState } from "react";
import "./Contact.scss";
import PageBanner from "../layouts/PageBanner";
import aboutContact from "../../assets/about-hero.jpg";

const API = "https://api.ainfotech.com.np";

const Toast = ({ show, variant = "success", title, message, onClose, duration = 5000 }) => {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => onClose?.(), duration);
    return () => clearTimeout(t);
  }, [show, duration, onClose]);

  return (
    <div
      className={`avena-toast avena-toast--${variant} ${show ? "is-visible" : ""}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="avena-toast__icon">
        {variant === "success" && <i className="bi bi-check-circle" aria-hidden="true" />}
        {variant === "error" && <i className="bi bi-x-circle" aria-hidden="true" />}
        {variant === "info" && <i className="bi bi-info-circle" aria-hidden="true" />}
      </div>
      <div className="avena-toast__content">
        <div className="avena-toast__title">{title}</div>
        {message && <div className="avena-toast__message">{message}</div>}
      </div>
      <button className="avena-toast__close" onClick={onClose} aria-label="Close notification">
        <i className="bi bi-x" aria-hidden="true" />
      </button>
      <div className="avena-toast__progress" />
    </div>
  );
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // toast state
  const [toast, setToast] = useState({
    show: false,
    variant: "success", // 'success' | 'error' | 'info'
    title: "",
    message: "",
  });

  const showToast = (variant, title, message = "") =>
    setToast({ show: true, variant, title, message });
  const hideToast = () => setToast((t) => ({ ...t, show: false }));

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // quick client validation
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      showToast("error", "Missing fields", "Please fill Name, Email and Message.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to submit");

      showToast("success", "Message sent!", "Thanks — we’ll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e2) {
      showToast("error", "Submission failed", e2.message || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="avena-contactpage">
      {/* Floating Toast Container */}
      <div className="avena-toastContainer">
        <Toast
          show={toast.show}
          variant={toast.variant}
          title={toast.title}
          message={toast.message}
          onClose={hideToast}
          duration={5000}
        />
      </div>

      <PageBanner
        title="Contact Us"
        bg={aboutContact}
        crumbs={[{ label: "Avena", href: "/" }, { label: "Contact" }]}
      />

      <section className="avena-contactSimple">
        <div className="container">
          <div className="row g-5 align-items-start">
            {/* LEFT */}
            <div className="col-12 col-lg-5">
              <h2 className="avena-contactSimple__title mt-3 mb-3">Get In Touch</h2>
              <p className="avena-contactSimple__lead mb-4">
                We’re here to assist you! Please reach out with any questions,
                feedback, or project inquiries.
              </p>

              <ul className="list-unstyled m-0 p-0 avena-contactSimple__list">
                <li className="d-flex align-items-start">
                  <i className="bi bi-telephone fs-4 me-3" />
                  <div>
                    <div className="avena-contactSimple__itemTitle">Phone:</div>
                    <div className="avena-contactSimple__itemText">+977 9841126140</div>
                  </div>
                </li>

                <li className="d-flex align-items-start">
                  <i className="bi bi-envelope fs-4 me-3" />
                  <div>
                    <div className="avena-contactSimple__itemTitle">Email Address:</div>
                    <div className="avena-contactSimple__itemText">info@ainfotech.com.np</div>
                  </div>
                </li>

                <li className="d-flex align-items-start">
                  <i className="bi bi-geo-alt fs-4 me-3" />
                  <div>
                    <div className="avena-contactSimple__itemTitle">Location:</div>
                    <div className="avena-contactSimple__itemText">
                      Banasthali-16, Kathmandu
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            {/* RIGHT: Card form */}
            <div className="col-12 col-lg-7">
              <div className="avena-contactSimple__card p-4 p-md-5">
                <h4 className="avena-contactSimple__cardTitle mb-4">Send Message</h4>

                <form onSubmit={onSubmit} noValidate>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        name="name"
                        className="form-control avena-contactSimple__input"
                        placeholder="Name"
                        value={form.name}
                        onChange={onChange}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="email"
                        name="email"
                        className="form-control avena-contactSimple__input"
                        placeholder="Email"
                        value={form.email}
                        onChange={onChange}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        name="phone"
                        className="form-control avena-contactSimple__input"
                        placeholder="Phone"
                        value={form.phone}
                        onChange={onChange}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        name="subject"
                        className="form-control avena-contactSimple__input"
                        placeholder="Subject"
                        value={form.subject}
                        onChange={onChange}
                      />
                    </div>
                    <div className="col-12">
                      <textarea
                        rows={6}
                        name="message"
                        className="form-control avena-contactSimple__input"
                        placeholder="Message"
                        value={form.message}
                        onChange={onChange}
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn avena-contactSimple__btn mt-4"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner" aria-hidden="true" /> Submitting…
                      </>
                    ) : (
                      "SUBMIT NOW"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="avena-contactMap">
        <div className="container">
          <div className="avena-contactMap__wrap">
            <iframe
              title="Map - 27.723107,85.2966667"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3531.996306400987!2d85.2969503!3d27.7239278!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb19f654a68d1d%3A0x85a131ba40c1d1d6!2sAvena%20Infotech%20Pvt.%20Ltd.!5e0!3m2!1sen!2snp!4v1737970000000"
              width="100%"
              height="450"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
